import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRunState, type PublicRunState } from '../services/publicRuns';
import type { RunState } from '../api/types';
import { useWebSocket, type WsStatus } from './useWebSocket';

/**
 * Payload pushed by the backend on the run_state_change WS event.
 * See src/backend/app/services/notify.py — notify_run_state_change().
 */
interface RunStateChangePayload {
  run_id: string;
  organization_id: string;
  previous_state: string;
  new_state: RunState;
  changed_at: string | null;
}

interface UseParticipantRunStateOptions {
  /**
   * The participant's org slug — used only to reach the run WebSocket, which
   * is an org-scoped *path* but guest-authorized by design (`ws.py`). The
   * REST run-state read does not need it.
   */
  orgSlug: string | undefined;
  runId: string | undefined;
  /** Disable the fetch + WS without unmounting. */
  enabled?: boolean;
  /** Fires once per real transition observed through the WS channel. */
  onStateChange?: (next: RunState, previous: RunState | null) => void;
}

interface UseParticipantRunStateResult {
  run: PublicRunState | null;
  state: RunState | null;
  wsStatus: WsStatus;
  loading: boolean;
}

/**
 * Participant-side, **read-only** counterpart of `useRunState` (#242).
 *
 * - Fetches the run lifecycle via `GET /api/public/runs/{runId}/state`, which
 *   is authorized by team membership — so a guest gets the run name + state
 *   without the coordinator-scoped `/api/orgs/...` run endpoints that 403 them.
 * - Subscribes to the run WS for `run_state_change` and applies it locally
 *   (then re-fetches `/state` so the transition timestamps stay in sync). The
 *   WS path is org-scoped but guest-authorized by design, so it is reused
 *   rather than duplicated.
 * - Exposes **no** transition helpers — participants never start/pause/end a
 *   run. `RunDashboard` keeps `useRunState` for the coordinator.
 *
 * `loading` always resolves deterministically (even when disabled, when
 * `runId` is missing, or for an already-ended run), so a shell gating a
 * spinner on it never hangs.
 */
export function useParticipantRunState({
  orgSlug,
  runId,
  enabled = true,
  onStateChange,
}: UseParticipantRunStateOptions): UseParticipantRunStateResult {
  // Keep the latest callback in a ref so a fresh inline function each render
  // doesn't rebuild the WS handler.
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const [run, setRun] = useState<PublicRunState | null>(null);
  const [loading, setLoading] = useState(true);

  const canFetch = enabled && !!runId;

  const refresh = useCallback(async () => {
    if (!canFetch) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const fresh = await fetchRunState(runId!);
    setRun(fresh);
    setLoading(false);
  }, [canFetch, runId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const canWs = enabled && !!orgSlug && !!runId;
  const { status: wsStatus } = useWebSocket({
    path: canWs ? `/api/orgs/${orgSlug}/runs/${runId}/ws` : '',
    enabled: canWs,
    handlers: {
      run_state_change: (data) => {
        const payload = data as RunStateChangePayload;
        // Defensive — the backend scopes by channel already.
        if (payload.run_id !== runId) return;
        setRun((prev) => {
          const previousState = prev?.state ?? null;
          if (previousState !== payload.new_state) {
            // Fire from the WS handler (not a reactive effect) so
            // react-hooks/set-state-in-effect stays happy.
            onStateChangeRef.current?.(payload.new_state, previousState);
          }
          return prev ? { ...prev, state: payload.new_state } : prev;
        });
        // Pull the other timestamps that changed with the transition.
        void refresh();
      },
    },
  });

  return {
    run,
    state: run?.state ?? null,
    wsStatus,
    loading,
  };
}
